import { EllipsisIcon, Maximize2Icon, MessageSquareIcon } from "lucide-react";
import type { ReactElement, ReactNode, Ref } from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import type { WorkspaceRecordListItem } from "@/lib/mlx-workspace-client";

/*
 * The record table's cells and phone list at rest, as markup. RecordTableLens
 * and its cells keep the live machinery (editing, menus, virtualization,
 * mutations and load-more) and render through these, so fixture records
 * compose the same rows. Values arrive formatted.
 */

type RecordSummary = Pick<
  WorkspaceRecordListItem,
  "title" | "discussionCommentCount"
>;

/** A record's row actions: its menu, the details shortcut, and the discussion with its comment count. */
export function RecordRowActionsView({
  record,
  disabled = false,
  onView,
  onDiscuss,
  menu = (trigger) => trigger,
}: {
  record: RecordSummary;
  /** While an archive or delete of the record is pending. */
  disabled?: boolean;
  onView?: () => void;
  onDiscuss?: () => void;
  /** Wraps the actions button in the record's menu; without one it shows at rest. */
  menu?: (trigger: ReactElement) => ReactNode;
}) {
  return (
    <div className="flex items-center justify-center gap-0.5">
      {menu(
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label="Record actions"
          title="Record actions"
          disabled={disabled}
          className="text-muted-foreground hover:text-foreground"
        >
          <EllipsisIcon />
        </Button>,
      )}
      <Button
        size="icon-xs"
        variant="ghost"
        aria-label={`Open record details for ${record.title || "record"}`}
        title="Open record details"
        disabled={disabled}
        className="text-muted-foreground hover:text-foreground"
        onClick={onView}
      >
        <Maximize2Icon />
      </Button>
      <RecordDiscussionButton record={record} onDiscuss={onDiscuss} />
    </div>
  );
}

/** Opens a record's discussion; a bubble counts its comments once there are any. */
export function RecordDiscussionButton({
  record,
  onDiscuss,
}: {
  record: RecordSummary;
  onDiscuss?: () => void;
}) {
  const count = record.discussionCommentCount;
  const commentLabel = count === 1 ? "1 comment" : `${count} comments`;
  return (
    <Button
      size="icon-xs"
      variant="ghost"
      aria-label={`Open discussion for ${record.title || "record"}, ${commentLabel}`}
      title="Open discussion"
      className="relative overflow-visible text-muted-foreground hover:text-foreground"
      onClick={onDiscuss}
    >
      <MessageSquareIcon />
      {count > 0 ? (
        <span
          aria-hidden
          className="absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-3xs font-semibold leading-none tabular-nums text-primary-foreground shadow-sm ring-1 ring-background"
        >
          {count}
        </span>
      ) : null}
    </Button>
  );
}

/** A record's title in the table's title column, while it is not being edited. */
export function RecordTitleCellView({ title }: { title: string }) {
  return (
    <span
      className="block truncate px-1 text-sm font-medium text-foreground"
      title={title || undefined}
    >
      {title}
    </span>
  );
}

/** A field's table cell while it is not being edited: the formatted value, with its text as the tooltip. */
export function EditableCellView({
  title,
  children,
}: {
  /** The value as plain text. */
  title?: string;
  /** The formatted value. */
  children?: ReactNode;
}) {
  return (
    <span
      className="block truncate text-sm text-foreground"
      title={title || undefined}
    >
      {children}
    </span>
  );
}

/** The record list below `sm`: cards over the channel canvas, each in a `pb-2` row, or the empty message. */
export function MobileRecordListView({
  scrollRef,
  height,
  emptyMessage = null,
  footer,
  children,
}: {
  scrollRef?: Ref<HTMLDivElement>;
  /** A virtualized list's total height; a static list takes its cards' height. */
  height?: number;
  /** Shown in place of cards when there are no records. */
  emptyMessage?: string | null;
  /** Below the cards: the load-more control in the application. */
  footer?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      ref={scrollRef}
      className="mlx-floating-panel-scroll-surface flex min-h-0 flex-1 flex-col overflow-y-auto bg-[var(--channel-canvas)] p-3 sm:hidden"
    >
      <div className="relative shrink-0" style={{ height }}>
        {children}
      </div>
      {emptyMessage !== null ? <EmptyState>{emptyMessage}</EmptyState> : null}
      {footer}
    </div>
  );
}

/** A record as a phone card: its title opens it, its actions sit beside it, and its first fields follow. */
export function MobileRecordCardView({
  title,
  fields,
  actions,
  onOpen,
}: {
  title: string;
  /** The fields to preview, each with its value formatted. */
  fields: { key: string; label: string; value: ReactNode }[];
  actions?: ReactNode;
  onOpen?: () => void;
}) {
  return (
    <article className="rounded-lg border border-border/70 bg-surface px-3 py-2.5 shadow-sm">
      <div className="flex min-w-0 items-start gap-2">
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={onOpen}
        >
          <h2 className="truncate text-sm font-semibold text-foreground">
            {title}
          </h2>
        </button>
        {actions}
      </div>
      {fields.length ? (
        <dl className="mt-2 grid gap-1.5">
          {fields.map((field) => (
            <div
              key={field.key}
              className="grid grid-cols-[minmax(88px,0.42fr)_minmax(0,1fr)] gap-2 text-xs"
            >
              <dt className="truncate text-muted-foreground">{field.label}</dt>
              <dd className="min-w-0 truncate text-foreground">{field.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </article>
  );
}
